import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { buildPageJsonLd } from './json-ld';
import { getPageMetaResolved } from './page-meta';
import { SITE_CONFIG } from './site-config';

const MANAGED_JSON_LD_ID = 'seo-jsonld';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    document.head.appendChild(element);
  }

  element.href = href;
}

export default function SeoHead() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = getPageMetaResolved(pathname);

    document.title = meta.title;
    document.documentElement.lang = SITE_CONFIG.language;

    upsertMeta('name', 'description', meta.description);
    upsertMeta('name', 'robots', meta.robots);
    upsertMeta('name', 'author', SITE_CONFIG.name);
    upsertMeta('name', 'theme-color', '#212529');

    if (meta.keywords) {
      upsertMeta('name', 'keywords', meta.keywords);
    }

    upsertLink('canonical', meta.canonical);

    upsertMeta('property', 'og:type', meta.ogType);
    upsertMeta('property', 'og:site_name', SITE_CONFIG.name);
    upsertMeta('property', 'og:locale', SITE_CONFIG.locale);
    upsertMeta('property', 'og:url', meta.canonical);
    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('property', 'og:description', meta.description);
    upsertMeta('property', 'og:image', meta.ogImage);
    upsertMeta('property', 'og:image:alt', meta.ogImageAlt);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', meta.title);
    upsertMeta('name', 'twitter:description', meta.description);
    upsertMeta('name', 'twitter:image', meta.ogImage);
    upsertMeta('name', 'twitter:image:alt', meta.ogImageAlt);

    let jsonLd = document.getElementById(MANAGED_JSON_LD_ID);
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.id = MANAGED_JSON_LD_ID;
      jsonLd.setAttribute('type', 'application/ld+json');
      document.head.appendChild(jsonLd);
    }

    jsonLd.textContent = JSON.stringify(buildPageJsonLd(meta, meta.canonical));
  }, [pathname]);

  return null;
}
